import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {toggle} from 'opticks'
import {equals, includes, last, map, toLower} from 'ramda'

import {Text} from '@daedalus/atlas/Text'

import {Offer} from '../../../../types/partnersApi/types'
import {hasFreeCancellationPenalty} from '../../../helpers/offer'

export interface Props {
  offer: Offer
  providerCode: string
  /** Whether to render the labels in the alternative color  */
  isAlternativeColor?: boolean
}

const RoomLabelsText = styled(Text)<{
  isAlternativeColor: boolean
}>(
  ({theme, isAlternativeColor}) => css`
    ${theme.typography.text.bodyXS}
    color: ${isAlternativeColor
      ? theme.colors.content.neutral.c600
      : theme.colors.content.featured.c500};
    white-space: ${isAlternativeColor ? 'nowrap' : 'normal'};
  `
)

const RoomLabelsItem = styled('span')(
  ({theme}) => css`
    display: inline-block;
    margin-right: ${theme.layout.spacing.s200}px;
  `
)

const Dot = styled(RoomLabelsText)(
  ({theme}) => css`
    display: inline-block;
    margin-left: ${theme.layout.spacing.s200}px;
  `
)
const roomLabels = defineMessages({
  freeCancellation: {
    id: 'hotelOffer.freeCancellation',
    defaultMessage: 'FREE cancellation'
  },
  payLater: {
    id: 'hotelOffer.payLater',
    defaultMessage: 'Pay later'
  },
  payAtProperty: {
    id: 'rate.payAtProperty',
    defaultMessage: 'Pay at property'
  },
  allInclusive: {
    id: 'hotelOffer.allInclusive',
    defaultMessage: 'Breakfast included, Lunch included, Dinner included'
  },
  breakfast: {
    id: 'hotelOffer.breakfast',
    defaultMessage: 'Breakfast included'
  },
  lunch: {
    id: 'hotelOffer.lunch',
    defaultMessage: 'Lunch included'
  },
  dinner: {
    id: 'hotelOffer.dinner',
    defaultMessage: 'Dinner included'
  },
  fullBoard: {
    id: 'hotelOffer.fullBoard',
    defaultMessage: 'Full board meal'
  },
  halfBoard: {
    id: 'hotelOffer.halfBoard',
    defaultMessage: 'Half board meal'
  },
  meals: {
    id: 'hotelOffer.meals',
    defaultMessage: 'Breakfast included, Lunch included, Dinner included'
  }
}) as Record<string, {id: string; defaultMessage: string}>

export const getPrioritizedMeals = (meals: string[]) => {
  if (meals && includes('allinclusive', map(toLower, meals))) {
    return ['allInclusive']
  }

  return meals || []
}

export const RoomLabels = ({
  offer,
  providerCode,
  isAlternativeColor = false
}: Props) => {
  const {formatMessage} = useIntl()

  const {cancellationPenalties, package: amenities} = offer

  const hasFreeCancellation = hasFreeCancellationPenalty(cancellationPenalties)

  const isPayAtPropertyExperiment = toggle(
    'e20e8bcc-pay-at-property',
    false,
    true
  )

  const roomLabelItems = {
    freeCancellation: hasFreeCancellation,
    payLater:
      amenities.canPayLater &&
      !(isPayAtPropertyExperiment && providerCode === 'EPS'),
    payAtProperty:
      amenities.canPayLater &&
      isPayAtPropertyExperiment &&
      providerCode === 'EPS'
  } as Record<string, boolean>

  const translatedRoomLabels = Object.keys(roomLabelItems).reduce(
    (out, key) => {
      if (roomLabelItems[key] && roomLabels[key]) {
        return out.concat(formatMessage(roomLabels[key]))
      }

      return out
    },
    [] as string[]
  )

  const translatedMealsLabels = getPrioritizedMeals(amenities.amenities).map(
    meal => meal && roomLabels[meal] && formatMessage(roomLabels[meal])
  )

  const translatedLabels = ([] as string[]).concat(
    translatedRoomLabels,
    translatedMealsLabels
  )

  if (translatedLabels.length <= 0) return null

  return (
    <RoomLabelsText isAlternativeColor={isAlternativeColor}>
      {translatedLabels.map(label => (
        <RoomLabelsItem key={label}>
          {label}
          {!equals(last(translatedLabels), label) && (
            <Dot isAlternativeColor={isAlternativeColor} as="span">
              ·
            </Dot>
          )}
        </RoomLabelsItem>
      ))}
    </RoomLabelsText>
  )
}
